<template>
  <BaseLoader v-if="!$store.getters.isLoaded(['catalog', 'products', 'shelves', 'orders']) || !sources" />
  <div v-else>
    <div v-if="!purchaseSource">
      <h1 class="h2 mb-3">Purchase</h1>
      <div class="list-group">
        <router-link
          class="list-group-item list-group-item-action"
          v-for="source in sources"
          :key="source.id"
          :to="{ name: 'purchase', params: { purchaseSource: source.id } }"
        >
          {{ source.label }}
        </router-link>
      </div>
    </div>
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h3 mb-0">{{ sources.find(s => s.id === purchaseSource).label }}</h1>
        <div class="d-flex justify-content-between align-items-center">
          <button
            class="btn btn-sm btn-outline-danger mr-2 text-nowrap"
            @click="resetPurchase"
            v-if="productsInCartCount > 0"
          >
            Reset {{ productsInCartCount }} {{ productsInCartCount > 1 ? 'Purchases' : 'Purchase' }}
          </button>
          <select class="custom-select" v-model="filter">
            <option :value="null">All</option>
            <option value="demandFromOrders">Red Items</option>
            <option value="purchased">Purchased</option>
          </select>
        </div>
      </div>
      <div v-if="!missingItemsByCategories || missingItemsByCategories.length === 0">
        <p v-if="filter === 'purchased'" class="alert alert-warning">Nothing purchased yet</p>
        <p class="alert alert-success" v-else>
          Nothing to buy
        </p>
      </div>
      <div v-else v-for="category in missingItemsByCategories" :key="category.categoryId">
        <li class="list-group-item list-group-item-secondary pt-1 pb-1">
          <small>{{ category.categoryLabel }}</small>
        </li>
        <li v-for="item in category.items" :key="item.productId" class="list-group-item d-flex align-items-center">
          <div class="flex-grow-1 d-flex align-items-center w-100">
            <span
              class="purchase__product-img flex-shrink-0  mr-3"
              :style="`background-image: url(https://storage.googleapis.com/emma-static/products/${item.img}_256.jpg);`"
              @click="detailLayerProductId = item.productId"
            />
            <div class="flex-grow-1">
              <div>
                <span>{{ item.brandName }} {{ item.label }}</span>
                <span class="badge badge-pill badge-warning ml-2" v-if="item.temporarilyUnavailable"
                  >Temp. Unavailable</span
                >
              </div>
              <small v-if="item.additionalLabel">{{ item.additionalLabel }}, </small>
              <small>{{ item.packageSize }} {{ item.packageUnit }}, {{ item.productId }}</small>
            </div>
          </div>
          <div style="font-size: 1.125rem;" class="text-nowrap p-2" @click="setPurchase(item.productId)" role="button">
            <span
              class="badge badge-secondary badge-pill"
              :class="{
                'badge-success': item.inCart >= item.demandFromOrders && item.inCart >= item.rebuyAmount,
                'badge-warning': item.inCart >= item.demandFromOrders && item.inCart < item.rebuyAmount,
                'badge-danger': item.inCart < item.demandFromOrders
              }"
            >
              {{ item.inCart }} / <span v-if="item.demandFromOrders">{{ item.demandFromOrders }} - </span
              >{{ item.rebuyAmount }}
            </span>
          </div>
        </li>
      </div>
    </div>
    <Layer
      :set="(product = $store.state.products.find(p => p.id === detailLayerProductId))"
      v-if="detailLayerProductId"
      @close="detailLayerProductId = null"
      :title="`${product.brandName} ${product.label}`"
      padding="1rem"
    >
      <div class="d-flex flex-column align-items-center">
        <div
          :style="`background-image: url(https://storage.googleapis.com/emma-static/products/${product.img}_256.jpg);`"
          class="purchase__detail-img"
        />
        <strong class="mt-3">{{ product.brandName }} {{ product.label }} {{ product.additionalLabel }}</strong>
        <p>{{ product.packageSize }} {{ product.packageUnit }} (ca. {{ formatCurrency(product.referencePrice) }})</p>
      </div>
    </Layer>
  </div>
</template>

<script>
import { db } from '@/shared/firebase'
import Layer from '@/shared/Layer'
import { ItemsInStock, MissingItems } from '@/helpers/Calculator'

export default {
  components: { Layer },
  data() {
    return {
      detailLayerProductId: null,
      filter: null,
      productsInCart: null,
      purchaseSubscription: null,
      purchaseSource: null,
      sources: null
    }
  },
  created() {
    this.purchaseSource = this.$router.currentRoute.params.purchaseSource
    this.purchaseSubscription = db.doc(`private/purchase`).onSnapshot(doc => {
      const { productsInCart, sources } = doc.data()
      const filteredSources = {}
      Object.keys(sources).forEach(sourceKey => {
        if (sources[sourceKey].showInPurchase) filteredSources[sourceKey] = sources[sourceKey]
      })
      this.sources = _.orderBy(
        Object.keys(filteredSources).map(id => {
          return { ...sources[id], id }
        }),
        [source => source.label.toLowerCase()]
      )
      this.productsInCart = productsInCart || {}
    })
  },
  beforeDestroy() {
    this.purchaseSubscription()
  },
  beforeRouteUpdate(to, from, next) {
    this.purchaseSource = to.params.purchaseSource
    next()
  },
  computed: {
    missingItemsByCategories() {
      let { orders, shelves, catalog, products } = this.$store.state
      let missingFromOrders = MissingItems(orders, shelves)
      const itemsInStock = ItemsInStock(shelves)
      const flatCategories = getFlatCategories(catalog)
      let missingItems = []
      products = products.filter(p => p.source === this.purchaseSource && p.live && !p.temporarilyUnavailable)
      products.forEach(product => {
        const inCart = this.productsInCart[product.id]?.amount || 0
        const inStock = itemsInStock[product.id] || 0
        const demandFromOrders = missingFromOrders[product.id] || 0
        const demandfromRebuyAmount = inStock < product.minStock ? product.rebuyAmount : 0
        if (this.filter === 'purchased' && inCart === 0) return null
        if (this.filter === 'demandFromOrders' && demandFromOrders <= inCart) return null
        if (demandFromOrders || demandfromRebuyAmount) {
          missingItems.push({
            ...product,
            demandFromOrders,
            demandfromRebuyAmount,
            productId: product.id,
            inStock,
            inCart
          })
        }
      })
      if (missingItems.length === 0) return null
      let categories = _.groupBy(missingItems, 'categoryId')
      categories = Object.keys(categories)
        .sort()
        .map(categoryId => {
          const items = categories[categoryId].map(item => {
            item.inCart = this.productsInCart[item.productId]?.amount || 0
            return item
          })
          return {
            categoryId,
            categoryLabel: flatCategories.find(c => c.id === categoryId).label,
            items
          }
        })
      return categories
    },
    productsInCartCount() {
      return Object.keys(this.productsInCart).length
    }
  },
  methods: {
    setPurchase(productId) {
      const product = this.$store.state.products.find(p => p.id === productId)
      const amount = prompt(`Enter purchase amount for ${product.brandName} ${product.label}`)
      if (amount && !isNaN(amount)) {
        db.doc('private/purchase').update({
          [`productsInCart.${productId}`]: {
            amount
          }
        })
      }
    },
    resetPurchase() {
      const confirmed = confirm(`Are you sure to reset ${this.productsInCartCount} items?`)
      if (confirmed) {
        db.doc(`private/purchase`)
          .update({
            productsInCart: {}
          })
          .then(() => {
            this.productsInCart = {}
          })
      }
    },
    showDetailLayer(productId) {
      console.log(productId)
    }
  }
}

const getFlatCategories = catalog => {
  const categories = []
  const getCategories = catalog => {
    Object.keys(catalog.categories).forEach(categoryId => {
      const category = catalog.categories[categoryId]
      categories.push({
        label: category.label,
        id: categoryId,
        product: category.products
      })
      if (category.categories) getCategories({ categories: category.categories })
    })
  }
  getCategories(catalog)
  return _.orderBy(categories, ['id'])
}
</script>

<style lang="scss" scoped>
.purchase {
  &__product-img {
    width: 4rem;
    height: 4rem;
    background-position: center center;
    background-size: cover;
    display: inline-block;
  }
  &__detail-img {
    width: 256px;
    height: 256px;
    background-position: center center;
    background-size: cover;
  }
}
</style>
